import { defineStore } from 'pinia';
import axios from 'axios';
import { buildCompetitiveLandscapeSeries, shouldUseDynamicTopCompetitors } from '@/utils/geo/competitiveLandscape';
import { formatCalendarDate, resolveCreationDate, toCalendarDate } from '@/utils/geo/dateRanges';

export const useAiBrandMonitorStore = defineStore('aiBrandMonitor', {
		state: () => ({
			loading: false,
			questions: [],
			kpis: [],
			actors: [],
			scoreCardData: [],
			dates: [],
			selectedDate: [],
			AiMarketId: null,
			startDateString: null,
			endDateString: null,
			competitorLabelId: null,
			labelIds: [],
			currentScoreBucket: null,
		}),
	getters: {
		positionsBetween_0_3: (state) => {
			return state.questions.filter(question => question.client_position >= 0 && question.client_position <= 2).length;
		},
		positionsBetween_3_10: (state) => {
			return state.questions.filter(question => question.client_position > 2 && question.client_position <= 10).length;
		},
		positionsBiggerThan_10: (state) => {
			return state.questions.filter(question => question.client_position > 10 || question.client_position == null).length;
		}
	},
		actions: {
		    updateScoreCardData(scoreBucket = null, endDate = null, { labelIds = null, competitorLabelId = null } = {}) {
				const resolvedScoreBucket = scoreBucket ?? this.currentScoreBucket;
				const resolvedEndDate = endDate ?? this.endDateString;
				const resolvedLabelIds = labelIds ?? this.labelIds;
				const resolvedCompetitorLabelId = competitorLabelId ?? this.competitorLabelId;
				this.scoreCardData = buildCompetitiveLandscapeSeries({
					actors: this.actors,
					scoreBucket: resolvedScoreBucket || {},
					endDate: resolvedEndDate,
					useDynamicTopCompetitors: shouldUseDynamicTopCompetitors({
						competitorLabelId: resolvedCompetitorLabelId,
						labelIds: resolvedLabelIds,
					}),
				});
			},
		    async fetchAiData(marketId, competitorLabel = null) {
	            if (this.loading) return;
            
            this.loading = true;

            try {
                if (this.AiMarketId != marketId) {
                    this.AiMarketId = marketId;
                    this.selectedDate = [];
                }

                // Store the competitor label ID for later use
                this.competitorLabelId = competitorLabel;

                await this.fetchAIDates(marketId);
                
                if (this.dates.length > 0) {
                    // Only set default if selectedDate is empty or invalid
                    if (this.selectedDate.length === 0 || !Array.isArray(this.selectedDate) || this.selectedDate.length !== 2) {
                        const lastDate = toCalendarDate(this.dates[this.dates.length - 1]);
                        this.selectedDate = [lastDate, lastDate];
                    } else {
                        // Validate that the persisted dates are still valid (convert to Date objects if needed)
                        if (this.selectedDate[0] && this.selectedDate[1]) {
                            // Convert persisted dates to Date objects
                            // Pinia persist serializes Date objects to ISO strings, so we need to handle both
                            let fromDate, toDate;
                            
                            if (this.selectedDate[0] instanceof Date) {
                                fromDate = this.selectedDate[0];
                            } else if (typeof this.selectedDate[0] === 'string') {
                                // Serialized Date string from Pinia persist
                                fromDate = new Date(this.selectedDate[0]);
                            } else if (this.selectedDate[0].creation_date) {
                                // Date object from API
                                fromDate = new Date(this.selectedDate[0].creation_date * 1000);
                            } else {
                                fromDate = new Date(this.selectedDate[0]);
                            }
                            
                            if (this.selectedDate[1] instanceof Date) {
                                toDate = this.selectedDate[1];
                            } else if (typeof this.selectedDate[1] === 'string') {
                                // Serialized Date string from Pinia persist
                                toDate = new Date(this.selectedDate[1]);
                            } else if (this.selectedDate[1].creation_date) {
                                // Date object from API
                                toDate = new Date(this.selectedDate[1].creation_date * 1000);
                            } else {
                                toDate = new Date(this.selectedDate[1]);
                            }
                            
                            // Check if dates are valid
                            if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
                                const lastDate = toCalendarDate(this.dates[this.dates.length - 1]);
                                this.selectedDate = [lastDate, lastDate];
                            } else {
                                this.selectedDate = [fromDate, toDate];
                            }
                        } else {
                            const lastDate = toCalendarDate(this.dates[this.dates.length - 1]);
                            this.selectedDate = [lastDate, lastDate];
                        }
                    }

                    const startDate = formatCalendarDate(this.selectedDate[0]);
                    const endDate = formatCalendarDate(this.selectedDate[1]);

	                    if (startDate && endDate) {
	                        this.startDateString = startDate;
	                        this.endDateString = endDate;

	                        await Promise.all([
	                            this.fetchTableData(marketId, this.startDateString, this.endDateString, competitorLabel),
	                            this.fetchActors(marketId, competitorLabel, this.labelIds),
	                            this.fetchKPIS(marketId, this.startDateString, this.endDateString, competitorLabel, this.labelIds),
	                        ]);
	                    } else {
	                        await this.fetchTableData(marketId, null, null, competitorLabel);
	                    }
                } else {
                    await this.fetchTableData(marketId, null, null, competitorLabel);
                }
                
            } finally {
                this.loading = false;
            }
        },
			async fetchAIDates(marketId) {
				const response = await axios.post(
				'vuejs/get_ai_geo_dates',
				{
					market_id: marketId,
				},
				{ withCredentials: true },
			);

				if (response.data.success) {
					this.dates = response.data.data;
				}
			},
			async fetchActors(marketId, competitorLabel = null, labelIds = null) {
				const response = await axios.post(
					'vuejs/geo_names',
					{
						market_id: marketId,
						competitor_label_id: competitorLabel,
						label_ids: labelIds ?? this.labelIds,
					},
					{ withCredentials: true },
				);

				if (response.data.success) {
					this.actors = response.data?.data?.actors || [];
					this.updateScoreCardData(null, null, {
						labelIds: labelIds ?? this.labelIds,
						competitorLabelId: competitorLabel,
					});
				}
			},
	        async fetchTableData(marketId, date_from = null, date_to = null, competitorLabel = null) {
            const creationDateFrom = resolveCreationDate(date_from, this.dates);
            const creationDateTo = resolveCreationDate(date_to, this.dates);
            
            const response = await axios.post(
                'vuejs/get_ai_geo',
                {
                    market_id: marketId,
                    date_range_start: creationDateFrom ?? date_from ?? null,
                    date_range_end: creationDateTo ?? date_to ?? null,
                    competitor_label_id: competitorLabel,
                    label_ids: this.labelIds,
                },
                { withCredentials: true },
            );

            if (response.data.success) {
                
                const uniqueQuestions = response.data.data.filter((question, index, self) => 
                    index === self.findIndex(q => q.question_id === question.question_id)
                );
                
                this.questions = uniqueQuestions;
            }
        },
		async fetchKPIS(marketId, date_from, date_to, competitorLabel = null, labelIds = null) {
			const creationDateFrom = resolveCreationDate(date_from, this.dates);
			const creationDateTo = resolveCreationDate(date_to, this.dates);
			const toIsoDate = (dateStr) => {
				if (!dateStr || typeof dateStr !== 'string') return null;
				const [day, month, year] = dateStr.split('-');
				if (!day || !month || !year) return null;
				return `${year}-${month}-${day}`;
			};

			const getActorMetric = (map, actorId = 0) => {
				if (!map || typeof map !== 'object') return 0;
				const rawValue = map[actorId] ?? map[String(actorId)] ?? 0;
				return Number(rawValue) || 0;
			};

			const response = await axios.post(
				'vuejs/geo_scores',
				{
					market_id: marketId,
					start_date: creationDateFrom ?? toIsoDate(date_from),
					end_date: creationDateTo ?? toIsoDate(date_to),
					competitor_label_id: competitorLabel,
					label_ids: labelIds,
				},
				{ withCredentials: true },
			);

				if (response.data.success) {
					const startData = response.data?.data?.start_date?.chatgpt || {};
					const endData = response.data?.data?.end_date?.chatgpt || {};

				const mapToKpi = (payload) => ({
					global_percentage: (Number(payload?.visibility_score) || 0) * 100,
					number_of_mentions: getActorMetric(payload?.n_mentions_by_actor, 0),
					number_of_sources_on_client_url: getActorMetric(payload?.n_source_by_actor, 0),
					average_position: 0,
				});

					this.kpis = [mapToKpi(endData), mapToKpi(startData)];
					this.currentScoreBucket = endData;
					this.updateScoreCardData(endData, date_to, {
						labelIds: labelIds ?? this.labelIds,
						competitorLabelId: competitorLabel,
					});
				}
			},
		setLabelIds(labelIds) {
			this.labelIds = Array.isArray(labelIds) ? labelIds : [];
		},
		async addQuestion(marketId, question) {
			try {
				const response = await axios.post(
					'vuejs/add_ai_geo_question',
					{
						market_id: marketId,
						questions: [question],
					},
					{ withCredentials: true },
				);
				return response;
			}
			catch (error) {
				console.error('Error adding question:', error);
			}
		},
		async deleteQuestion(market_id, question) {
			try {
				const response = await axios.post(
					'vuejs/delete_ai_geo_question',
					{
						market_id: market_id,
						questions: [question],
					},
					{ withCredentials: true },
				).then(response => {
					this.fetchTableData(market_id, this.startDateString, this.endDateString, this.competitorLabelId);
					this.fetchKPIS(market_id, this.startDateString, this.endDateString, this.competitorLabelId, this.labelIds);
				});
				return response;
			}
			catch (error) {
				console.error('Error deleting question:', error);
			}
		},
		async assignKeyword(market_id, addedKeyword, deletedKeyword) {
			try {
				const response = await axios.post(
					'vuejs/assign_ai_geo_keyword',
					{
						market_id: market_id,
						added_keyword: addedKeyword,
						deleted_keyword: deletedKeyword,
					},
					{ withCredentials: true },
				).then(response => {
					this.fetchTableData(market_id, this.startDateString, this.endDateString, this.competitorLabelId);
					this.fetchKPIS(market_id, this.startDateString, this.endDateString, this.competitorLabelId, this.labelIds);
				});
				return response;
			}
			catch (error) {
				console.error('Error assigning keyword:', error);
			}
		},
		async autoAssignKeywords(market_id, questionsId) {
			try {
				const response = await axios.post(
					'vuejs/auto_assign_ai_geo_keywords',
					{
						market_id: market_id,
						questions_id: questionsId,
					},
					{ withCredentials: true },
				);
				return response;
			}
			catch (error) {
				console.error('Error auto-assigning keywords:', error);
			}
		},
		async assignLabels(market_id, addedLabel, deletedLabel) {
			try {
				const response = await axios.post(
					'vuejs/assign_ai_geo_labels',
					{
						market_id: market_id,
						added_label: addedLabel,
						deleted_label: deletedLabel,
					},
					{ withCredentials: true },
				).then(response => {
					this.fetchTableData(market_id, this.startDateString, this.endDateString, this.competitorLabelId);
					this.fetchKPIS(market_id, this.startDateString, this.endDateString, this.competitorLabelId, this.labelIds);
				});
				return response;
			}
			catch (error) {
				console.error('Error assigning labels:', error);
			}
		},
		async autoAssignLabels(market_id, questionsId) {
			try {
				const response = await axios.post(
					'vuejs/auto_assign_ai_geo_labels',
					{
						market_id: market_id,
						questions_id: questionsId,
					},
					{ withCredentials: true },
				);
				return response;
			}
			catch (error) {
				console.error('Error auto-assigning labels:', error);
			}
		},
		async setKeywordQuestion(marketId, questionId, keywordId) {
			try {
				const response = await axios.post(
					'vuejs/set_keyword_question',
					{
						market_id: marketId,
						question_id: questionId,
						keyword_id: keywordId,
					},
					{ withCredentials: true },
				);
				return response;
			}
			catch (error) {
				console.error('Error adding question:', error);
			}
		},
			resetData() {
				this.questions = [];
				this.kpis = [];
				this.actors = [];
				this.scoreCardData = [];
				this.dates = [];
				// Don't reset selectedDate to preserve user selection
				// this.selectedDate = [];
				this.AiMarketId = null;
				this.startDateString = null;
				this.endDateString = null;
				this.competitorLabelId = null;
				this.currentScoreBucket = null;
			this.labelIds = [];
			this.loading = false;
		}
	},
	persist: true,
});
